import React, { useEffect, useState } from 'react'
import { Avatar, Chip, Icon, SelectInline, InputInline, Field } from '@arnsbogroup/react-voicearchive'
import InlineLanguageSelect from './components/InlineLanguageSelect/index'
import Actions from './components/Actions'
import { FormattedTime, FormattedDate } from 'react-intl'

const SupplierInfo = ( {supplier, onChangeHandler, currentUser} ) => {
  const [statusOptions, setStatusOptions] = useState([])
  const [genderOptions, setGenderOptions] = useState([])
  const [typeOptions, setTypeOptions] = useState([])
  const [partnerOptions, setPartnerOptions] = useState([])
  const [supplierLocalDateState, setSupplierLocalDateState] = useState(new Date());
  const priorityIcons = [
    { slug: 'voicestar', icon: 'star', className: 'text-primary', title: 'Voice Star' },
    { slug: 'custom_rates', icon: 'funnel-dollar', className: 'text-purple-600', title: 'Custom rates' },
    { slug: 'easy_to_work_with', icon: 'award', className: 'text-warning', title: 'Easy to work with' },
  ]
  const skills = [
    {
      label: 'acting',
      value: 'ACTING',
    },
    {
      label: 'live session audio',
      value: 'LIVE_SESSION',
    },
    {
      label: 'live session playback',
      value: 'ADVANCED_LIVE_SESSION',
    },
  ]

  useEffect(() => {
    Object.entries({
      '/rest/v2/supplierstatus': setStatusOptions,
      '/rest/v2/gender': setGenderOptions,
      '/rest/v2/suppliertype?pageSize=99': setTypeOptions,
      '/rest/v2/partner': setPartnerOptions
    }).map(([endpoint, setState]) => {
      fetch(endpoint)
      .then((response) => response.json())
      .then((data) => {
        setState(data.map((value) => ({value: value.uid, label: value.name})))
      })
      .catch((err) => {
        toastr.error(err)
      })
    })
  }, [])

  useEffect(() => {
    const interval = setInterval(() => setSupplierLocalDateState(new Date()), 1000);
    return () => {
      clearInterval(interval);
    };
  }, supplier);
  
  const SupplierStatus = ({ status }) => (
    <Chip size="sm" state={{
      'BLOCKED': 'error',
      'INACTIVE': 'error',
      'ACTIVE': 'success',
      'APPLICANT': 'warning',
      'ONLINE': 'success',
      'IN REVIEW': 'warning',
      'ACCEPTED': 'warning',
      'ONBOARDING': 'warning',
      'REJECTED': 'error',
    }[status.toUpperCase()] ?? undefined}>
      <span className="capitalize">
        { status ? status.toLowerCase() : '' }
      </span>
    </Chip>
  )

  const copySupplierUrl = () => {
    navigator.clipboard.writeText(window.location.href).then(function() {
      toastr.success('Supplier link copied')
    }, function(err) {
      toastr.error(err)
    });
  }

  const isDay = () => {
    const supplierLocalTimeStamp = new Date(supplier.local_time.date)

    const night = new Date();
    night.setHours(22);
    night.setMinutes(0);
    night.setMilliseconds(0);

    const morning = new Date();
    morning.setHours(8);
    morning.setMinutes(0);
    morning.setMilliseconds(0);

    return supplierLocalTimeStamp < night && supplierLocalTimeStamp > morning
  }

  if (!supplier) {
    return null
  }

  return (
    <div className="p-4 text-center flex flex-col md:flex-row">
      <div className="flex flex-col justify-center space-y-4 items-center md:w-1/6">
        <Avatar
          src={supplier.contact ? supplier.contact.image : null}
          size="xl"
          initials={supplier.firstname.charAt(0) + supplier.lastname.charAt(0)}
        />

        <div className="flex space-x-2">
          {priorityIcons.map(({ icon, slug, className, title }) => (
            <Icon 
              title={title}
              className={`hover:cursor-help text-xl ${supplier.priorities.find((priority) => priority.slug == slug) ? className : "text-gray-200"}`}
              icon={["fas", icon]} 
            />
          ))}
        </div>
        <div>
          <Icon 
            title={isDay() ? `It is day time for ${supplier.firstname}` : `Be aware! It is night time for ${supplier.firstname}`}
            size="xs"
            className={`${isDay() ? 'text-warning' : 'text-primary'} mr-1`}
            icon={['fas', isDay() ? 'sun' : 'moon']}
          />
          <span
            className="text-sm font-medium text-gray-600"
            title={`Current local time in ${supplier.firstname}"s time zone`}
          >
            <FormattedTime
              value={supplierLocalDateState}
              timeZone={supplier.timezone}
            />
          </span>
        </div>
        <div>
          {!!supplier?.latest_activity ? (
            <div>
              <label>Last activity:</label>
              <p
                className="text-sm font-medium text-gray-600"
              >
                <FormattedDate value={new Date(supplier.latest_activity * 1000)} /> <FormattedTime value={new Date(supplier.latest_activity * 1000)} />
              </p>
            </div>
            ) : (
              <span
                className="text-sm font-medium text-gray-600"
                title={`No activity in the past 30 days`}
              >
                No activity in the past 30 days
              </span>
            )
          }
        </div>
      </div>

      <div className="ml-2 grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3 flex-initial w-full text-left order-3 md:order-none">
        <div className="space-y-4">
          <div className="flex items-end space-x-2">
            <div className="flex-initial">
              <InputInline 
                labelClassName="text-xs"
                label="ID"
                placeholder="ID"
                value={ supplier.userid }
                onChange={({ target: { value } }) => {
                  onChangeHandler({ ...supplier, userid: value })
                }}
              />
            </div>
            <div className="flex-initial">
              <SelectInline
                labelClassName="text-xs"
                placeholder="Status"
                value={ statusOptions.find(status => supplier.status.uid == status.value) }
                options={ statusOptions }
                onChange={(status) => { onChangeHandler({ ...supplier, status: {uid: status.value, name: status.label} }) }}
                disabled={!currentUser.roles.includes("sourcing") && supplier.contact}
              >
                <SupplierStatus status={supplier.status.name}/>
              </SelectInline>
            </div>
          </div>

          <div className="flex space-x-2">
            <div className="flex-initial">
              <InputInline
                labelClassName="text-xs"
                placeholder="First name"
                label="First name"
                value={ supplier.firstname }
                onChange={({ target: { value } }) => {
                  onChangeHandler({ ...supplier, firstname: value })
                }}
              />
            </div>
            <div className="flex-initial">
              <InputInline 
                labelClassName="text-xs"
                placeholder="Last name"
                label="Last name"
                value={ supplier.lastname }
                onChange={({ target: { value } }) => {
                  onChangeHandler({ ...supplier, lastname: value })
                }}
              />
            </div>
          </div>

          <InputInline 
            labelClassName="text-xs"
            placeholder="Email"
            label="Email"
            value={ supplier.email }
            onChange={({ target: { value } }) => {
              onChangeHandler({ ...supplier, email: value })
            }}
          />

          <SelectInline
            labelClassName="text-xs"
            label="Gender"
            placeholder="Gender"
            value={ supplier.gender ? genderOptions.find(gender => supplier.gender == gender.value) : [] }
            options={ genderOptions }
            onChange={(gender) => { 
              onChangeHandler({ ...supplier, gender: gender.value }) 
            }}
          >
            <div className="flex">
              <div>{genderOptions.find(gender => supplier.gender == gender.value) ? genderOptions.find(gender => supplier.gender == gender.value).label : ""}</div>
              <Icon 
                className={`ml-1 my-auto ${['text-yellow-600', 'text-green-600', 'text-purple-600'][supplier.gender]}`} 
                icon={supplier.gender ? ['fas', ['genderless', 'mars', 'venus'][supplier.gender]] : null}
              /> 
            </div>
          </SelectInline>
        </div>

        <div className="space-y-4">
          <InlineLanguageSelect
            label="Languages"
            placeholder="Languages"
            value={supplier.languages}
            onChange={(languages) => { onChangeHandler({ ...supplier, languages: languages }) }}
          />

          <SelectInline
            labelClassName="text-xs"
            containerProps={{className:"mb-2"}}
            label="Types"
            placeholder="Types"
            value={ supplier.types.map((type) => ({value: type.uid, label:type.name})) }
            options={ typeOptions }
            onChange={(types) => { onChangeHandler({ ...supplier, types: types.map((type) => ({uid: type.value, name: type.label})) }) } } 
            isMulti
          />

          <SelectInline
            labelClassName="text-xs"
            containerProps={{className:"mb-2"}}
            label="Attributes"
            placeholder="Attributes"
            value={ skills.filter((skill) => 
              supplier.attributes.includes(skill.value)
            ) }
            options={ skills }
            onChange={(skills) => onChangeHandler({ ...supplier, attributes: (skills ? skills.map((skill) => skill.value) : []) }) }
            disabled={!currentUser.roles.includes("sourcing")}
            isMulti
          />

          <SelectInline
            labelClassName="text-xs"
            containerProps={{className:"mb-2"}}
            label="Partner"
            placeholder="Partner"
            value={ partnerOptions.find(partner => supplier.partner == partner.value) }
            options={ partnerOptions }
            onChange={(partner) => { onChangeHandler({ ...supplier, partner: partner.value }) }}
            disabled={!currentUser.roles.includes("sourcing")}
          />
        </div>

        <div className="space-y-4">
          { supplier.agency?.name &&
            <Field label="Agency" labelClassName="text-xs">
              { supplier.agency.name }
            </Field>
          }

          { supplier?.company != "" &&
            <Field label="Company" labelClassName="text-xs">
              { supplier.company }
            </Field>
          }

          <Field label="Direct Link" labelClassName="text-xs">
            <div
              className="group flex items-center space-x-1 cursor-alias"
              onClick={copySupplierUrl}
            >
              <label className="truncate mr-1 cursor-pointer group-hover:text-primary">
                { window.location.href }
              </label>
              <Icon 
                className="text-gray-400 group-hover:text-primary"
                icon={["fas", "copy"]} 
              />
            </div>
          </Field>
        </div>
      </div>

      <div className="order-2 py-4 md:py-0 md:px-4 md:order-none flex justify-center items-center md:w-1/6">
        <Actions
          supplier={supplier}
          currentUserRoles={currentUser.roles}
        />
      </div>
    </div>
  )
}

export default SupplierInfo
